import axios from "axios";
import {
  getValueByKey,
  removeLoggedInMemberDetails,
  removeLoggedInUserDetails,
  removeLoggedInVendorDetails,
} from "../utils/authUtils";

/* Everything goes through the gateway now.
 * The gateway picks the service from the first path segment and strips it:
 *    /api/*     ->  member_portal  (8001)
 *    /vendor/*  ->  vendor_portal  (8002)
 */
// const GATEWAY_URL = process.env.REACT_APP_API;
//const GATEWAY_URL = "http://beta.hramembers.com/hra-api";
//const GATEWAY_URL = "http://localhost:8000";
// const GATEWAY_URL = "http://192.168.1.14:8000";
// const GATEWAY_URL = process.env.REACT_APP_API || "https://hramembers.com/hra-api";
const GATEWAY_URL = "https://hramembers.com/hra-api";

/* Also use these for non-axios URLs: <img src>, PDFs, downloads */
export const MEMBER_API_BASE_URL = `${GATEWAY_URL}/api`;
export const VENDOR_API_BASE_URL = `${GATEWAY_URL}/vendor`;

/* Where an expired vendor token sends the user.
 * The vendor portal UI shares the /vendor prefix with the member-facing vendor
 * list ("/vendor", "/vendor/:id"); static segments outrank :id, so the vendor
 * pages match first. */
const VENDOR_LOGIN_ROUTE = "/vendor/login";

/* Second path segment of every vendor portal screen. "/vendor" and "/vendor/:id"
 * are the member-facing vendor list, so the prefix alone cannot tell the two
 * apart - only these static segments mean "logged in as a vendor user". */
const VENDOR_PORTAL_SEGMENTS = [
  "dashboard",
  "members",
  "support-tickets",
  "documents",
  "login",
  "forgot-password",
  "reset-password",
];

const isVendorPortalPage = () => {
  const [, first, second] = window.location.pathname.split("/");
  return first === "vendor" && VENDOR_PORTAL_SEGMENTS.includes(second);
};

/*
 * invokeApi(method, url, data, headers, api)
 *
 *   api omitted / anything else  ->  member portal   (/api)      <- default
 *   api === "vendor"             ->  vendor portal   (/vendor)
 *
 * Existing call sites need no change - they keep hitting the member portal.
 */
const invokeApi = (method, url, data, headers, api) => {
  const isVendor = api === "vendor";
  const baseUrl = isVendor ? VENDOR_API_BASE_URL : MEMBER_API_BASE_URL;

  /* Which token to send is decided by who is logged in, not by which portal is
   * being called. The admin area has no vendor token, and vendor_portal routes
   * that use CommonAuth accept the admin token anyway. */
  const isAdminArea = window.location.pathname.split("/")[1] === "admin";

  /* A vendor portal screen sends its vendor token even when the call goes to the
   * member portal (/api) - member_portal signs with the same secret and its
   * CommonAuth accepts a VENDOR_AUTH token, so shared endpoints like
   * /memberDataExport work for both. */
  const isVendorPage = isVendorPortalPage();

  let accessToken;
  if (isAdminArea) {
    accessToken = getValueByKey("adminAccessToken");
  } else if (isVendor || isVendorPage) {
    accessToken = getValueByKey("vendorAccessToken");
  } else {
    accessToken = getValueByKey("memberAccessToken");
  }

  return axios({
    method: method,
    url: baseUrl + url,
    headers: {
      ...headers,
      Authorization: "Bearer " + accessToken,
    },
    data: data,
    /* read back in the interceptor - the URL path cannot tell the portals apart.
     * A vendor page counts as vendor even on a member portal URL, so an expired
     * token sends the user back to the vendor login instead of the member one. */
    portal: isVendor || isVendorPage ? "vendor" : "member",
  })
    .then((response) => {
      return response.data;
    })
    .catch((err) => {
      /* err.response is undefined when the gateway itself is unreachable.
       * Return the shape the sagas already expect so they do not crash. */
      if (!err.response) {
        return {
          status: false,
          message: "Server is not reachable. Please try again.",
          data: {},
        };
      }
      return err.response.data;
    });
};

axios.interceptors.response.use(
  function (response) {
    return response;
  },
  function (error) {
    if (error.response && error.response.status === 401) {
      if (error.config && error.config.portal === "vendor") {
        /* the session ended without a Logout click - close the activity trail
         * first, while the (expired) token is still in storage to send. The
         * route is unauthenticated, so this cannot 401 back into here. */
        const vendorToken = getValueByKey("vendorAccessToken");
        if (vendorToken) {
          axios({
            method: "post",
            url: `${VENDOR_API_BASE_URL}/sessionExpired`,
            headers: { Authorization: "Bearer " + vendorToken },
          }).catch(() => {});
        }
        removeLoggedInVendorDetails();
        if (window.location.pathname !== VENDOR_LOGIN_ROUTE) {
          window.location.href = VENDOR_LOGIN_ROUTE;
        }
      } else if (window.location.pathname !== "/login") {
        if (window.location.pathname.split("/")[1] === "admin") {
          window.location.href = "/admin/login";
          removeLoggedInUserDetails();
        } else {
          removeLoggedInMemberDetails();
          window.location.href = "/login";
        }
      }
    }
    return Promise.reject(error);
  }
);

export { invokeApi };
